/**
 * Meta Pixel (browser) loader + typed event helpers.
 *
 * The pixel can only run on the app-owned public shell, never inside the page
 * embed (that's a sandboxed, opaque-origin iframe). All public surfaces that
 * fire events — the page view, the checkout, the thank-you page — go through
 * here so the base snippet is injected exactly once and events are always
 * scoped to a specific pixel id via `trackSingle`.
 */

import { sendCapiCopy } from "./capi";

type Fbq = ((...args: unknown[]) => void) & {
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: (...args: unknown[]) => void;
  callMethod?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Pixel ids we've already called fbq('init') for, so we never double-init. */
const initialised = new Set<string>();

/**
 * Run `cb` at the next idle gap, but no later than ~1s. Used to defer the heavy
 * fbevents.js off the critical render path while still guaranteeing it lands
 * quickly (a page that never goes idle still fires at the 1s cap). `requestIdle-
 * Callback`'s own `timeout` gives the bound; older Safari without it falls back
 * to a plain 1s timer. Same idea as Next.js `<Script strategy="lazyOnload">`.
 */
function scheduleWithin1s(cb: () => void): void {
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (ric) ric(cb, { timeout: 1000 });
  else setTimeout(cb, 1000);
}

/** Inject the standard Meta Pixel base code once (no auto PageView). */
function ensureBaseLoaded(): Fbq | null {
  if (typeof window === "undefined") return null;
  if (window.fbq) return window.fbq;

  const fbq: Fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue?.push(args);
  };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;

  // Defer the heavy fbevents.js (104KB + the signals config it pulls) off the
  // critical render path — loaded eagerly it competes with LCP and first input
  // (measured ~700ms TBT on a WP-import storefront). The fbq() stub above keeps
  // queuing init/track calls synchronously, so nothing is lost: when the script
  // loads it drains the queue and fires the buffered PageView, within ~1s.
  scheduleWithin1s(() => {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  });
  return fbq;
}

/**
 * Advanced matching: identifiers handed to `fbq('init')` so every event from
 * this pixel carries them. Sent RAW — fbevents.js normalises and hashes them
 * itself, and pre-hashing would double-hash. Keys are Meta's, so a caller maps
 * once and both sides agree with the server's CAPI user_data.
 */
export interface PixelUserData {
  em?: string | null;
  ph?: string | null;
  fn?: string | null;
  ln?: string | null;
  ct?: string | null;
  st?: string | null;
  zp?: string | null;
  country?: string | null;
  external_id?: string | null;
}

/** Pixel ids we've already supplied advanced-matching data for. */
const matched = new Set<string>();

function cleanUserData(data: PixelUserData): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(data)) {
    const value = (v ?? "").trim();
    if (value) out[k] = value;
  }
  return out;
}

/**
 * Initialise a pixel id (idempotent). Returns false when tracking is off.
 *
 * `userData` upgrades the pixel to advanced matching. A storefront init happens
 * long before the buyer identifies themselves, so the first init is anonymous
 * and this re-inits ONCE later — when the checkout finally knows who they are —
 * to attach the identifiers to every subsequent event. Re-initing again per
 * render would spam fbevents.js, hence the `matched` guard.
 */
export function initPixel(pixelId: string | null | undefined, userData?: PixelUserData): boolean {
  if (!pixelId) return false;
  const fbq = ensureBaseLoaded();
  if (!fbq) return false;

  const identifiers = userData ? cleanUserData(userData) : {};
  const hasIdentifiers = Object.keys(identifiers).length > 0;

  if (!initialised.has(pixelId)) {
    if (hasIdentifiers) {
      fbq("init", pixelId, identifiers);
      matched.add(pixelId);
    } else {
      fbq("init", pixelId);
    }
    initialised.add(pixelId);
  } else if (hasIdentifiers && !matched.has(pixelId)) {
    fbq("init", pixelId, identifiers);
    matched.add(pixelId);
  }
  return true;
}

export interface TrackOptions {
  /** Shared id so the matching server-side (CAPI) event is deduplicated. */
  eventID?: string;
  /** Attach advanced-matching identifiers before firing (see initPixel). */
  userData?: PixelUserData;
  /**
   * false: no Conversions API copy from the browser — for a pixel with no token
   * here (the platform's), or an event whose copy is sent later with more to go
   * on (a checkout's InitiateCheckout, once the buyer has typed their email).
   */
  capi?: false;
  /**
   * Browser-side guard key. The first call with a given key fires; later calls
   * with the same key are dropped. Needed because React StrictMode (dev) invokes
   * effects twice and unstable effect deps can re-run them — without this the
   * same PageView/Purchase fires 2-3× in the browser even though Meta would
   * later dedupe by eventID. Keep keys stable per logical event (e.g. the order
   * id for a Purchase, pixel+path for a PageView).
   */
  dedupeKey?: string;
}

/** Logical events already fired this page load — see TrackOptions.dedupeKey. */
const firedKeys = new Set<string>();

/** A fresh event id, for an event no caller named: the key a server copy of it would share. */
function newEventId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
}

/**
 * Fire a standard event against ONE pixel. Every event carries an event id —
 * the caller's (a Purchase's is its order id, as the server's CAPI Purchase
 * uses) or a fresh one — so a server copy of it can be deduplicated by Meta;
 * returned for that copy, null when nothing fired. `dedupeKey` stops the same
 * event firing twice in the browser.
 */
export function pixelTrack(
  pixelId: string | null | undefined,
  event:
    | "PageView"
    | "ViewContent"
    | "AddToCart"
    | "InitiateCheckout"
    | "AddPaymentInfo"
    | "CompleteRegistration"
    | "Purchase",
  params?: Record<string, unknown>,
  options?: TrackOptions,
): string | null {
  if (!initPixel(pixelId, options?.userData)) return null;
  if (options?.dedupeKey) {
    if (firedKeys.has(options.dedupeKey)) return null;
    firedKeys.add(options.dedupeKey);
  }
  const eventID = options?.eventID ?? newEventId();
  window.fbq?.("trackSingle", pixelId, event, params ?? {}, { eventID });
  if (options?.capi !== false) sendCapiCopy(pixelId!, event, eventID, params, options?.userData);
  return eventID;
}

/**
 * A custom event (one Meta has no standard name for — e.g. a product list
 * opened) against ONE pixel. Shows in Events Manager under its own name and can
 * back a custom conversion; same guards as pixelTrack.
 */
export function pixelTrackCustom(pixelId: string | null | undefined, event: string, params?: Record<string, unknown>, options?: TrackOptions): string | null {
  if (!initPixel(pixelId, options?.userData)) return null;
  if (options?.dedupeKey) {
    if (firedKeys.has(options.dedupeKey)) return null;
    firedKeys.add(options.dedupeKey);
  }
  const eventID = options?.eventID ?? newEventId();
  window.fbq?.("trackSingleCustom", pixelId, event, params ?? {}, { eventID });
  if (options?.capi !== false) sendCapiCopy(pixelId!, event, eventID, params, options?.userData);
  return eventID;
}
